import type { Row, RowSelectionState } from "@tanstack/react-table";
import * as React from "react";

import { useWindowedPages } from "../../hooks/use-windowed-pages";
import { useContextMenuClaim } from "./data-table-overlay";
import type { DataTableGroup } from "./data-table-types";
import { useDataTableVirtualizer } from "./data-table-virtualizer";

const NOOP_FETCH_PAGE = () => {};

/**
 * One group's row window: the virtualizer over the shared scroll container
 * (with this group's own scroll margin), the offset-based page cache under it,
 * and the row lookups the renderers read through. Returns the refs the body
 * markup mounts so the measurements land on the right nodes.
 */
export function useDataTableGroupRows<TData>({
  group,
  rows,
  rowHeight,
  rowSelection,
  scrollContainerRef,
  isExpanded,
  isUngrouped,
  useInternalScroll,
  groupLoading,
  hadRows,
}: {
  group: DataTableGroup<TData>;
  rows: Row<TData>[];
  rowHeight: number;
  rowSelection: RowSelectionState | undefined;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  isExpanded: boolean;
  isUngrouped: boolean | undefined;
  useInternalScroll: boolean;
  groupLoading: boolean;
  /** The group has shown rows at some point: a reload is not a first paint. */
  hadRows: boolean;
}) {
  // -------------------------------------------------------------------------
  // Virtualizer (shared scroll container, per-group scrollMargin)
  // -------------------------------------------------------------------------

  // A Base UI menu root per row and per header cell is the single largest
  // block of scroll-frame work, torn down and rebuilt on every row recycle.
  // Rows carry only a pointerdown closure instead, handing the table's shared
  // menu host a thunk for their own items.
  const claimContextMenu = useContextMenuClaim();

  const groupBodyRef = React.useRef<HTMLDivElement | null>(null);
  const rowsContainerRef = React.useRef<HTMLTableSectionElement | null>(null);
  const [scrollMargin, setScrollMargin] = React.useState(0);

  // Measure offset from scroll container to rows container for the
  // virtualizer's scrollMargin.  Deferred to rAF so getBoundingClientRect()
  // runs after browser layout is computed (nearly free) rather than forcing
  // a synchronous reflow.  The cleanup cancels the previous frame's rAF so
  // only the latest render's measurement fires.  The computed offset is
  // stable during scroll (scrollTop and element position change equally),
  // so setScrollMargin is a no-op on scroll frames and won't loop.
  React.useEffect(() => {
    if (!isExpanded) return;
    const id = requestAnimationFrame(() => {
      const scrollEl = scrollContainerRef.current;
      const rowsEl = rowsContainerRef.current;
      if (!scrollEl || !rowsEl) return;
      const scrollRect = scrollEl.getBoundingClientRect();
      const rowsRect = rowsEl.getBoundingClientRect();
      const offset = Math.round(
        rowsRect.top - scrollRect.top + scrollEl.scrollTop,
      );
      setScrollMargin((prev) => (prev === offset ? prev : offset));
    });
    return () => cancelAnimationFrame(id);
    // Re-measure when expansion state or row count changes (layout shifts).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-measures on expansion and row count only; the refs are stable
  }, [isExpanded, rows.length]);

  // When a numeric totalCount is provided and exceeds loaded rows, size
  // the virtualizer to the full server total so the scrollbar reflects
  // the true dataset size.  Unloaded indices render skeleton placeholders.
  const virtualCount =
    typeof group.totalCount === "number" && group.totalCount > rows.length
      ? group.totalCount
      : rows.length;

  // The initial-load window with NO known content: no rows yet and no server
  // total. Nothing is proven, so the ungrouped table paints no column header
  // here — a header that appears and then yields to the empty state reads as
  // a flash. The settled state (rows or the empty state) paints once instead.
  // With a known totalCount the header stays put: the sized loading
  // placeholder already reserves the rows' space beneath it. A group loading
  // AGAIN keeps it too — the header was up, and taking it down is the flash.
  const headerSettling =
    isUngrouped && groupLoading && virtualCount === 0 && !hadRows;

  const virtualizer = useDataTableVirtualizer({
    count: virtualCount,
    rowHeight,
    scrollContainerRef,
    scrollMargin,
    enabled: useInternalScroll && isExpanded && virtualCount > 0,
  });

  // -------------------------------------------------------------------------
  // Offset-based page fetching
  // -------------------------------------------------------------------------

  // Ref for row selection so the scroll handler always sees latest state
  // without adding it as an effect dependency.
  const rowSelectionRef = React.useRef(rowSelection);
  rowSelectionRef.current = rowSelection;

  // Windowed paging: virtualizer sized to the full server total, only a window of
  // pages in memory. `useWindowedPages` owns the page cache, fetch scheduling,
  // eviction, and reset-on-variablesKey; `getWindowRow(index)` resolves the row
  // or undefined (→ skeleton). Pages holding selected rows are kept via `keepPage`.
  const PAGE_SIZE = group.pageSize ?? 50;
  const dataOffset = group.dataOffset ?? 0;

  const { getItem: getWindowRow, cacheVersion } = useWindowedPages<Row<TData>>({
    total: virtualCount,
    pageSize: PAGE_SIZE,
    pageItems: rows,
    dataOffset,
    variablesKey: group.variablesKey ?? "",
    fetchPage: group.fetchPage ?? NOOP_FETCH_PAGE,
    virtualizer,
    scrollElementRef: scrollContainerRef,
    enabled:
      useInternalScroll &&
      isExpanded &&
      !groupLoading &&
      !group.placeholder &&
      !!group.fetchPage &&
      virtualCount > 0,
    keepPage: (_pageStart, pageRows) => {
      const sel = rowSelectionRef.current;
      return sel ? pageRows.some((r) => sel[r.id]) : false;
    },
  });

  // The row-drag registry reads rows by group index through whichever window
  // the body renders from; `cacheVersion` refreshes it as pages load.
  const getRowAt = React.useCallback(
    (index: number) => (virtualizer ? getWindowRow(index) : rows[index]),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cacheVersion is the windowed cache's change signal
    [virtualizer, getWindowRow, rows, cacheVersion],
  );

  return {
    claimContextMenu,
    groupBodyRef,
    rowsContainerRef,
    virtualizer,
    virtualCount,
    headerSettling,
    getWindowRow,
    getRowAt,
    cacheVersion,
    pageSize: PAGE_SIZE,
  };
}
